import { useDepartmentStore, type CampaignData } from "@/features/department";
import { type DispatchContext, asRecord } from "./types";
import { appEvents } from "@/shared/lib/events";

/** Returns true if the tool was handled. */
export function dispatchGeoTool(toolName: string, ctx: DispatchContext): boolean {
  const { payload } = ctx;
  const state = useDepartmentStore.getState();

  switch (toolName) {
    case "start_geo_research":
      appEvents.publish({
        type: "agent-start-geo-research",
        geoInput: payload.geoInput as unknown,
      });
      return true;
    case "present_gap_analysis": {
      const geoScore = asRecord(payload.geoScore);
      const overallRaw = Number(geoScore.overall);
      const overall = Number.isFinite(overallRaw) ? Math.round(overallRaw) : 0;
      const aiShareRaw = Number(geoScore.aiShareOfVoice);
      const aiShareOfVoice = Number.isFinite(aiShareRaw) ? Math.round(aiShareRaw) : 0;
      const contentGaps = Array.isArray(payload.contentGaps) ? payload.contentGaps : [];
      const topCompetitors = Array.isArray(payload.topCompetitors) ? payload.topCompetitors : [];
      const competitorPreview = topCompetitors
        .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
        .slice(0, 3)
        .join(", ");

      state.updateCampaignData({
        geoScoreResult: payload.geoScore,
        contentGaps: payload.contentGaps,
        geoTopCompetitors: payload.topCompetitors,
      } as Partial<CampaignData>);
      state.setExecutionStep(2, "waiting");
      state.addMessage({
        role: "agent",
        content: `Gap analysis is ready: GEO score ${overall}/100, AI Share of Voice ${aiShareOfVoice}%, and ${contentGaps.length} prioritized issues. In the scorecard widget you can review severity-ranked gaps and competitor citations${competitorPreview ? ` (top: ${competitorPreview})` : ""}.`,
        widget: "geoScorecard",
        source: "tool_dispatch",
        dedupeKey: "geo:step2:gap_analysis_ready",
      });
      return true;
    }
    case "generate_schemas": {
      const schemas = Array.isArray(payload.schemas) ? payload.schemas : [];
      const existingCopy = Array.isArray(state.campaignData.copyUpgrades)
        ? state.campaignData.copyUpgrades
        : [];
      const schemaTypes = schemas
        .map((asset) => asRecord(asset).type)
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .slice(0, 3)
        .join(", ");

      state.updateCampaignData({ jsonLdAssets: payload.schemas } as Partial<CampaignData>);
      state.setExecutionStep(3, "waiting");
      state.addMessage({
        role: "agent",
        content: `Schema assets are ready: ${schemas.length} JSON-LD blocks${schemaTypes ? ` (${schemaTypes})` : ""}. In Asset Review, open the Schemas tab first; Copy rewrites currently has ${existingCopy.length} items.`,
        widget: "geoAssetReview",
        source: "tool_dispatch",
        dedupeKey: "geo:step3:schemas_ready",
      });
      return true;
    }
    case "generate_copy_upgrades": {
      const upgrades = Array.isArray(payload.upgrades) ? payload.upgrades : [];
      const schemaCount = Array.isArray(state.campaignData.jsonLdAssets)
        ? state.campaignData.jsonLdAssets.length
        : 0;
      state.updateCampaignData({ copyUpgrades: payload.upgrades } as Partial<CampaignData>);
      state.setExecutionStep(3, "waiting");
      state.addMessage({
        role: "agent",
        content: `Copy upgrades are ready: ${upgrades.length} rewrite${upgrades.length === 1 ? "" : "s"} with clear information-gain notes. In Asset Review, use the Copy rewrites tab for edits and keep Schemas (${schemaCount}) aligned.`,
        widget: "geoAssetReview",
        source: "tool_dispatch",
        dedupeKey: "geo:step3:copy_ready",
      });
      return true;
    }
    case "generate_entity_payload": {
      const entityPayload = asRecord(payload.entityPayload);
      const officialName =
        typeof entityPayload.officialName === "string" ? entityPayload.officialName : "Your brand";
      const targets = Array.isArray(entityPayload.distributionTargets)
        ? entityPayload.distributionTargets.length
        : 0;
      const dataPoints = Array.isArray(entityPayload.dataPoints)
        ? entityPayload.dataPoints.length
        : 0;

      state.updateCampaignData({ entityPayload: payload.entityPayload } as Partial<CampaignData>);
      state.setExecutionStep(4, "waiting");
      state.addMessage({
        role: "agent",
        content: `Entity authority kit is ready for ${officialName}: ${targets} platform targets and ${dataPoints} key proof points. In the checklist widget, review the bio, data points, and copy-ready distribution cards.`,
        widget: "geoEntityChecklist",
        source: "tool_dispatch",
        dedupeKey: "geo:step4:entity_ready",
      });
      return true;
    }
    case "generate_geo_implementation":
    case "generate_implementation_plan": {
      const implementationPack = asRecord(payload.implementationPack);
      const taskCount = Array.isArray(implementationPack.tasks)
        ? implementationPack.tasks.length
        : 0;
      const quickWins = Array.isArray(implementationPack.quickWins)
        ? implementationPack.quickWins.length
        : 0;
      const playbooks = Array.isArray(implementationPack.cmsPlaybooks)
        ? implementationPack.cmsPlaybooks.length
        : 0;
      state.updateCampaignData({
        geoImplementationPack: payload.implementationPack as any,
      } as Partial<CampaignData>);
      state.setExecutionStep(5, "waiting");
      state.addMessage({
        role: "agent",
        content: `Implementation pack is ready with ${taskCount} prioritized tasks, ${quickWins} quick wins, and ${playbooks} CMS playbooks. In the GEO Implementation widget, review Execution queue, Ready files, then CMS playbooks.`,
        widget: "geoImplementation",
        source: "tool_dispatch",
        dedupeKey: "geo:step5:implementation_ready",
      });
      return true;
    }
    default:
      return false;
  }
}
